import { Button , Input , Label , Skeleton } from "@roberttamas/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { EditDialog, SettingRow } from "@/components/setting-row";
import type { TranslationKey } from "@/i18n";
import { getTextOverride, getTranslations, saveTextOverride } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import { formatLocaleLabel } from "@/lib/locales";

/** Translation keys available for per-site override (same list as TranslationsPage). */
const OVERRIDE_KEYS: { key: string; labelKey: TranslationKey }[] = [
  { key: "upload.select.title", labelKey: "textOverrides.fields.uploadSelectTitle" },
  { key: "upload.select.subtitle", labelKey: "textOverrides.fields.uploadSelectSubtitle" },
  { key: "upload.review.title", labelKey: "textOverrides.fields.uploadReviewTitle" },
  { key: "upload.review.subtitle", labelKey: "textOverrides.fields.uploadReviewSubtitle" },
  { key: "upload.uploading.title", labelKey: "textOverrides.fields.uploadUploadingTitle" },
  { key: "upload.uploading.subtitle", labelKey: "textOverrides.fields.uploadUploadingSubtitle" },
  { key: "upload.confirmation.thanks", labelKey: "textOverrides.fields.confirmationThanks" },
  { key: "upload.confirmation.subtitle", labelKey: "textOverrides.fields.confirmationSubtitle" },
  { key: "bootstrap.archivedWarning", labelKey: "textOverrides.fields.bootstrapArchivedWarning" },
  { key: "bootstrap.expiredWarning", labelKey: "textOverrides.fields.bootstrapExpiredWarning" },
  { key: "gallery.emptyTitle", labelKey: "textOverrides.fields.galleryEmptyTitle" },
  { key: "gallery.emptySubtitle", labelKey: "textOverrides.fields.galleryEmptySubtitle" },
  { key: "gallery.endTitle", labelKey: "textOverrides.fields.galleryEndTitle" },
  { key: "gallery.endSubtitle", labelKey: "textOverrides.fields.galleryEndSubtitle" },
  { key: "download.title", labelKey: "textOverrides.fields.downloadTitle" },
];

interface Props {
  siteId: string;
  language: string;
  textOverrideId?: string;
}

export function TextOverridesRow({ siteId, language, textOverrideId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  // Load base texts (placeholders).
  const baseQuery = useQuery({
    queryKey: ["translations", language],
    queryFn: () => getTranslations({ variant: language }),
    staleTime: 5 * 60_000,
    enabled: !!language,
  });

  // Load existing override keys if the site has one.
  const overrideQuery = useQuery({
    queryKey: ["textOverride", textOverrideId],
    queryFn: () => getTextOverride({ textOverrideId: textOverrideId! }),
    staleTime: 60_000,
    enabled: !!textOverrideId,
  });

  const [values, setValues] = useState<Record<string, string>>({});
  const loading = baseQuery.isLoading || (!!textOverrideId && overrideQuery.isLoading);

  useEffect(() => {
    if (!loading) {
      const overrideKeys = overrideQuery.data?.keys ?? {};
      setValues(overrideKeys);
    }
  }, [loading, overrideQuery.data]);

  const mutation = useMutation({
    mutationFn: () => saveTextOverride({ siteId, keys: values }),
    onSuccess: async () => {
      toast.success(t("textOverrides.saved"));
      await queryClient.invalidateQueries({ queryKey: ["site", siteId] });
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  const baseKeys = baseQuery.data?.keys ?? {};
  const overrideCount = Object.values(values).filter((v) => v.trim().length > 0).length;
  const summary =
    overrideCount > 0
      ? t("textOverrides.summary", { count: overrideCount, language: formatLocaleLabel(language) })
      : t("textOverrides.summaryNone", { language: formatLocaleLabel(language) });

  return (
    <>
      <SettingRow
        title={t("textOverrides.title")}
        description={t("textOverrides.description")}
        value={<span className="text-muted-foreground">{summary}</span>}
        action={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={loading}
            onClick={() => setOpen(true)}
          >
            {t("common.edit")}
          </Button>
        }
      />

      <EditDialog
        open={open}
        onOpenChange={setOpen}
        title={t("textOverrides.dialogTitle")}
        description={t("textOverrides.dialogDescription")}
        onSave={() => mutation.mutate()}
        saving={mutation.isPending}
        saveDisabled={loading}
        saveLabel={t("textOverrides.saveLabel")}
        contentClassName="max-w-2xl"
      >
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {OVERRIDE_KEYS.map(({ key, labelKey }) => (
              <div key={key} className="space-y-1">
                <Label htmlFor={`override-${key}`} className="text-xs">
                  {t(labelKey)}
                  <code className="ml-2 rounded bg-muted px-1 py-0.5 font-mono text-[10px] text-muted-foreground">
                    {key}
                  </code>
                </Label>
                <Input
                  id={`override-${key}`}
                  placeholder={baseKeys[key] ?? ""}
                  value={values[key] ?? ""}
                  onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        )}
      </EditDialog>
    </>
  );
}
